import * as RadixDialog from '@radix-ui/react-dialog'
import { motion } from 'motion/react'
import { a, k, useCss } from 'kremling'
import React, { forwardRef, ReactNode } from 'react'

import { Card } from '@components/card.component'
import { ActionIcon } from '@components/mantine/action-icon.component'

type ModalSize = 'sm' | 'md' | 'lg'

type ModalProps = {
  children: ReactNode
  onClose?: () => void
  hideClose?: boolean
  size?: ModalSize
}

type ModalComponent = React.ForwardRefExoticComponent<
  ModalProps & React.RefAttributes<HTMLDivElement>
> & {
  Header: typeof Header
  Body: typeof Body
  Footer: typeof Footer
  BalancedFooter: typeof BalancedFooter
  Overlay: typeof Overlay
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  ({ children, onClose, hideClose = false, size = 'md' }, ref) => {
    const scope = useCss(css)

    return (
      <div
        {...scope}
        className="modal-container"
        ref={ref}
      >
        <motion.div
          key="modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <RadixDialog.Overlay className="modal-backdrop" />
        </motion.div>
        <RadixDialog.Content asChild>
          <motion.div
            key="modal-container"
            initial={{ opacity: 0, y: '5vh' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '-5vh' }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
          >
            <Card
              level="3"
              className={a('modal')
                .m('modal--sm', size === 'sm')
                .m('modal--md', size === 'md' || !size)
                .m('modal--lg', size === 'lg')}
            >
              {onClose && !hideClose && (
                <RadixDialog.Close asChild>
                  <ActionIcon
                    className="modal-close-button"
                    size="sm"
                    icon="times"
                    variant="tertiary"
                    aria-label="Close"
                  />
                </RadixDialog.Close>
              )}
              {children}
            </Card>
          </motion.div>
        </RadixDialog.Content>
      </div>
    )
  },
) as ModalComponent

type HeaderProps = {
  children: ReactNode
} & React.HTMLAttributes<HTMLDivElement>

const Header = ({ children, ...rest }: HeaderProps) => (
  <div
    className="modal__header"
    {...rest}
  >
    <RadixDialog.Title className="modal__title">{children}</RadixDialog.Title>
  </div>
)

type BodyProps = {
  children: ReactNode
  className?: string
} & React.HTMLAttributes<HTMLDivElement>

const Body = ({ children, className = '', ...rest }: BodyProps) => (
  <div
    className={a('modal__body', className)}
    {...rest}
  >
    {children}
  </div>
)

type FooterProps = {
  children: ReactNode
  className?: string
} & React.HTMLAttributes<HTMLDivElement>

const Footer = ({ children, className = '', ...rest }: FooterProps) => (
  <div
    className={a('modal__footer', className)}
    {...rest}
  >
    {children}
  </div>
)

type BalancedFooterProps = {
  children: ReactNode
} & React.HTMLAttributes<HTMLDivElement>

const BalancedFooter = ({ children, ...rest }: BalancedFooterProps) => (
  <div
    className="modal__balanced_footer"
    {...rest}
  >
    {children}
  </div>
)

type OverlayProps = {
  children: ReactNode
} & React.HTMLAttributes<HTMLDivElement>

const Overlay = ({ children, ...rest }: OverlayProps) => (
  <div
    className="modal__overlay"
    {...rest}
  >
    {children}
  </div>
)

Modal.Header = Header
Modal.Body = Body
Modal.Footer = Footer
Modal.BalancedFooter = BalancedFooter
Modal.Overlay = Overlay

const css = k`
  .modal-container {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 9000;
  }

  .modal-backdrop {
    background-color: rgba($color-grey-900, .2);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 0;
  }

  .modal__overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    z-index: 1000;
    align-items: center;
    justify-content: center;
    background-color: rgba(#fff, .9);
    border-radius: $base-border-radius;
    flex-direction: column;
    padding: 5rem;
  }

  .modal {
    position: relative;
    z-index: 1;
    border-radius: 0;
    max-height: calc(100vh - 1.6rem);
    display: flex;
    flex-direction: column;
  }

  .modal.modal--sm,
  .modal.modal--md,
  .modal.modal--lg {
    width: 100vw;
  }

  @media (min-width: $media1-width) {
    .modal {
      border-radius: $base-border-radius;
      max-height: calc(100vh - 3.2rem);
    }

    .modal.modal--sm {
      width: 34rem;
    }

    .modal.modal--md {
      width: 48rem;
    }

    .modal.modal--lg {
      width: 64rem;
    }
  }

  .modal__header {
    display: flex;
    align-items: center;
    min-height: 5rem;
    shrink: 0;
    padding: 1.2rem 1.6rem;
  }

  .modal-close-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
  }

  .modal__title {
    font-size: 2rem;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    gap: .8rem;
    align-items: center;
    font-weight: 500;
  }

  .modal__body {
    padding: .4rem 1.6rem;
    overflow-y: auto;
  }

  .modal__footer {
    text-align: right;
    padding: 1.6rem;
    shrink: 0;

    > button {
      margin-left: .8rem;
    }
  }

  .modal__balanced_footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.6rem;
    shrink: 0;

    > div button {
      margin-left: .8rem;
    }
  }
`
